import { useRef } from "react";
import { motion, useInView } from "motion/react";
import {
  CloudOff,
  FolderOutput,
  Lock,
  Megaphone,
  ScanBarcode,
  Smartphone,
  UserRound,
} from "lucide-react";
import { useI18n } from "@/i18n/I18nContext";

/** Order matches `messages.privacy.bullets` */
const PRIVACY_ICONS = [
  UserRound,
  CloudOff,
  Smartphone,
  FolderOutput,
  ScanBarcode,
  Megaphone,
] as const;

export function PrivacySection() {
  const { messages } = useI18n();
  const pr = messages.privacy;
  const ref = useRef<HTMLElement | null>(null);
  const inView = useInView(ref, { once: true });

  return (
    <section
      id="privacy"
      ref={ref}
      className="relative overflow-hidden bg-gradient-to-br from-[#111111] to-[#2a2a2a] py-32 text-white"
    >
      <motion.div
        className="absolute top-20 right-20 h-64 w-64 rounded-full bg-[#4CAF50] opacity-10 blur-3xl"
        animate={{
          scale: [1, 1.2, 1],
          opacity: [0.1, 0.15, 0.1],
        }}
        transition={{
          duration: 5,
          repeat: Infinity,
          ease: "easeInOut",
        }}
      />

      <div className="relative z-10 container mx-auto px-6 lg:px-12">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="mx-auto max-w-4xl text-center"
        >
          <motion.div
            animate={inView ? { scale: [0, 1.2, 1] } : {}}
            transition={{ duration: 0.6 }}
            className="mb-8 inline-block"
          >
            <div className="mx-auto flex h-24 w-24 items-center justify-center rounded-3xl bg-[#4CAF50]">
              <Lock className="h-12 w-12 text-white" />
            </div>
          </motion.div>

          <h2 className="mb-6 text-4xl lg:text-6xl">{pr.heading}</h2>

          <p className="mx-auto mb-12 max-w-2xl text-xl text-gray-300">
            {pr.body}
          </p>

          <div className="mx-auto grid w-full max-w-3xl grid-cols-2 gap-6">
            {pr.bullets.map((text, index) => {
              const Icon = PRIVACY_ICONS[index];
              if (!Icon) return null;
              const isLastOdd =
                index === pr.bullets.length - 1 && pr.bullets.length % 2 !== 0;
              const cardVisual =
                "flex min-h-[148px] flex-col rounded-2xl border border-white/10 bg-white/5 p-6 backdrop-blur-sm transition-colors duration-300 hover:bg-white/10";
              /** One column width: (grid width − gap) / 2 — matches sibling cells */
              const oneCol =
                "w-[calc((100%-1.5rem)/2)] max-w-[calc((100%-1.5rem)/2)] shrink-0";

              if (isLastOdd) {
                return (
                  <div
                    key={text}
                    className="col-span-2 flex w-full min-w-0 justify-center"
                  >
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.5, delay: index * 0.1 }}
                      className={`${cardVisual} ${oneCol} min-w-0 items-center text-center`}
                    >
                      <Icon className="mb-3 h-8 w-8 shrink-0 text-[#4CAF50]" />
                      <p className="text-lg leading-snug">{text}</p>
                    </motion.div>
                  </div>
                );
              }

              return (
                <motion.div
                  key={text}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className={`${cardVisual} w-full min-w-0 items-left text-center`}
                >
                  <Icon className="mb-3 h-8 w-8 shrink-0 text-[#4CAF50]" />
                  <p className="text-lg leading-snug">{text}</p>
                </motion.div>
              );
            })}
          </div>

          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.6, duration: 0.8 }}
            className="mx-auto mt-12 block max-w-xl rounded-2xl border border-[#4CAF50]/20 bg-[#4CAF50]/10 p-6 text-center"
          >
            <p className="text-sm text-gray-300">{pr.trustBanner}</p>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}
